 <!-- settings modal -->
<script setup lang="ts">
import {
  IonButtons,
  IonButton,
  IonContent,
  IonIcon,
  IonTitle,
  IonToolbar,
  modalController,
} from '@ionic/vue';
import { useIonRouter } from '@ionic/vue';
import { 
  closeOutline, 
  personOutline, 
  briefcaseOutline, 
  mailOutline, 
  logOutOutline,
  chevronForwardOutline
} from 'ionicons/icons';
import { useStore } from '@/stores/main';
import { featureNotAvailable } from '@/helpers';
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';

const { t } = useI18n();
const store = useStore();
const router = useIonRouter();
const isModalVisible = ref(false);

onMounted(() => {
  setTimeout(() => {
    isModalVisible.value = true;
  }, 100);
});

function close() {
  isModalVisible.value = false;
  setTimeout(() => {
    modalController.dismiss(null, 'cancel');
  }, 300); // Delay for exit animation
}

function previousProfiles() {
  featureNotAvailable();
}

async function logout() {
  isModalVisible.value = false;
 
    // Clear local storage, session storage, and cookies
    localStorage.clear();   // Clears all items in localStorage
    sessionStorage.clear(); // Clears all items in sessionStorage
    
    // Clears cookies by setting them with an expired date
    document.cookie.split(';').forEach((cookie) => {
      const cookieName = cookie.split('=')[0].trim();
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    });

    store.logout();
    await modalController.dismiss(null, 'cancel');
    router.replace('/login');

}
</script>

<template>
  <ion-content>
    <div class="modal-container" :class="{ 'visible': isModalVisible }">
      <!-- Header -->
      <ion-toolbar class="header-toolbar">
        <ion-title>{{ $t('settings.title') }}</ion-title>
        <ion-buttons slot="end">
          <ion-button @click="close" class="close-button">
            <ion-icon :icon="closeOutline" slot="icon-only" />
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
      
      <!-- Content -->
      <div class="modal-content">
        <div class="profile-section">
          <div class="section-header">
            <h2>{{ $t('settings.profile') }}</h2>
            <div class="section-divider"></div>
          </div>
          
          <div class="profile-fields">
            <!-- Name Field -->
            <div class="profile-field" :style="{'--delay': '0.1s'}">
              <div class="field-icon">
                <ion-icon :icon="personOutline"></ion-icon>
              </div>
              <div class="field-content">
                <label for="name">{{ $t('settings.name') }}</label>
                <div class="field-value">{{ `${store.firstName} ${store.lastName}` }}</div>
              </div>
            </div>
            
            <!-- Company Field -->
            <div class="profile-field" :style="{'--delay': '0.2s'}">
              <div class="field-icon">
                <ion-icon :icon="briefcaseOutline"></ion-icon>
              </div>
              <div class="field-content">
                <label for="company">{{ $t('settings.company') }}</label>
                <div class="field-value">{{ store.company }}</div>
              </div>
            </div>
            
            <!-- Email Field -->
            <div class="profile-field" :style="{'--delay': '0.3s'}">
              <div class="field-icon">
                <ion-icon :icon="mailOutline"></ion-icon>
              </div>
              <div class="field-content">
                <label for="email">{{ $t('settings.email') }}</label>
                <div class="field-value">{{ store.email }}</div>
              </div>
            </div>

            <!-- Language Field -->
            <div class="profile-field" :style="{'--delay': '0.4s'}">
              <div class="field-icon">
                <ion-icon :icon="personOutline"></ion-icon>
              </div>
              <div class="field-content">
                <label for="language">{{ $t('settings.language') }}</label>
                <LanguageSwitcher/>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Actions Section -->
        <div class="actions-section">
          <button @click="logout" class="logout-button">
            <ion-icon :icon="logOutOutline"></ion-icon>
            <span>{{ $t('settings.logout') }}</span>
          </button>
        </div>
      </div>
      
      <!-- Footer - Version Info -->
      <div class="modal-footer">
        <p class="version-text">Bwell v1.2.5</p>
      </div>
      
      <!-- Decorative Elements -->
      <div class="decorative-shape shape-1"></div>
      <div class="decorative-shape shape-2"></div>
    </div>
  </ion-content>

</template>

<style scoped>
ion-content {
  --background: rgba(0, 0, 0, 0.3);
}

.modal-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.95);
  width: 90%;
  max-width: 480px;
  max-height: 90vh;
  background: theme('colors.primary.light');
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  opacity: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
}

.modal-container.visible {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
}

/* Header Styles */
.header-toolbar {
  --background: theme('colors.primary.light');
  --color: theme('colors.primary.dark');
  --min-height: 64px;
  --padding-start: 20px;
  --padding-end: 10px;
  border-bottom: 1px solid rgba(123, 31, 162, 0.1);
}

ion-title {
  font-size: 20px;
  font-weight: 600;
}

.close-button {
  margin-right: 5px;
  --border-radius: 50%;
  --padding-start: 8px;
  --padding-end: 8px;
  transition: background-color 0.2s ease;
}

.close-button:hover {
  --background: rgba(123, 31, 162, 0.1);
}

/* Content Styles */
.modal-content {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 30px;
}

/* Profile Section */
.section-header {
  margin-bottom: 20px;
  position: relative;
}

.section-header h2 {
  color: theme('colors.primary.dark');
  font-size: 18px;
  font-weight: 500;
  margin: 0;
}

.section-divider {
  height: 2px;
  background: linear-gradient(to right, theme('colors.primary.dark'), transparent);
  width: 80px;
  margin-top: 8px;
}

.profile-fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.profile-field {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  opacity: 0;
  transform: translateY(10px);
  animation: fadeInUp 0.5s ease forwards;
  animation-delay: var(--delay, 0s);
}

.profile-field:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.field-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: rgba(123, 31, 162, 0.1);
  color: theme('colors.primary.dark');
  border-radius: 10px;
  flex-shrink: 0;
}

.field-icon ion-icon {
  font-size: 20px;
}

.field-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.field-content label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}

.field-value {
  font-size: 16px;
  color: #1f2937;
  font-weight: 400;
  word-break: break-word;
}

/* Actions Section */
.actions-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 10px;
}

.logout-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: white;
  color: #ef4444;
  border: none;
  border-radius: 30px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.logout-button:hover {
  background-color: #fef2f2;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.15);
  transform: translateY(-2px);
}

.logout-button:active {
  transform: translateY(0);
}

.logout-button ion-icon {
  font-size: 18px;
}

/* Footer Styles */
.modal-footer {
  padding: 12px 20px;
  text-align: center;
  border-top: 1px solid rgba(123, 31, 162, 0.1);
}

.version-text {
  font-size: 12px;
  color: #9ca3af;
  margin: 0;
}

/* Decorative Shapes */
.decorative-shape {
  position: absolute;
  border-radius: 50%;
  z-index: 0;
  opacity: 0.3;
  filter: blur(40px);
  pointer-events: none;
}

.shape-1 {
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgba(123, 31, 162, 0.1), rgba(123, 31, 162, 0.05));
  top: -80px;
  right: -80px;
}

.shape-2 {
  width: 150px;
  height: 150px;
  background: radial-gradient(circle, rgba(123, 31, 162, 0.1), rgba(123, 31, 162, 0.05));
  bottom: -50px;
  left: -50px;
}

/* Media Queries */
@media (max-width: 480px) {
  .modal-container {
    width: 95%;
    max-height: 95vh;
  }
  
  .field-icon {
    width: 36px;
    height: 36px;
  }
  
  .field-icon ion-icon {
    font-size: 18px;
  }
  
  .field-value {
    font-size: 15px;
  }
}
</style>